import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import forgotPassword from "@insite/client-framework/Store/Context/Handlers/ForgotPassword";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import GenericModal from "@sonic/CustomComponents/headless/components/GenericModal/GenericModal";
import TextField from "@sonic/Overrides/Components/TextField";
import React, { Dispatch, RefObject, SetStateAction, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css, FlattenSimpleInterpolation } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
});

interface OwnProps {
    isResetPasswordModalOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onClose: () => void;
}

const mapDispatchToProps = {
    resetPassword: forgotPassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SignInResetPasswordFormStyles {
    container?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: FlattenSimpleInterpolation;
}

export const signInResetPasswordFormStyles: SignInResetPasswordFormStyles = {
    userNameGridItem: {
        width: 12,
    },
    userNameTextField: {
        labelProps: {
            lineHeight: 1.5,
        },
    },
    descriptionGridItem: {
        width: 12,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    cancelButton: css`
        margin-right: 10px;
    `,
};

const cancleButtonStyles = css`
    margin-right: 10px;
    max-width: max-content;
`;

/**
 * @deprecated Use signInResetPasswordFormStyles instead.
 */
export const createListFormStyles = signInResetPasswordFormStyles;
const styles = signInResetPasswordFormStyles;

const SignInResetPasswordForm = ({
    onClose,
    resetPassword,
    validateReCaptcha,
    accountSettings,
    isResetPasswordModalOpen,
    setIsOpen,
}: Props) => {
    const [userName, setUserName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const { useEmailAsUserName } = accountSettings;
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const sendEmail = async (userName: string) => {
        if (!userName.trim()) {
            setErrorMessage(
                siteMessage(
                    "Field_Required",
                    useEmailAsUserName ? translate("Email") : translate("Username"),
                ) as string,
            );
            return;
        }
        if (useEmailAsUserName && !validateEmail(userName)) {
            setErrorMessage(siteMessage("ResetPassword_EmailAddress_Validation") as string);
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "ForgotPassword",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        resetPassword({
            userName,
            onSuccess: () => {
                toasterContext.addToast({
                    body: useEmailAsUserName
                        ? siteMessage("ResetPassword_EmailResetPasswordEmailSent")
                        : siteMessage("ResetPassword_ResetPasswordEmailSent"),
                    messageType: "success",
                });
                onClose();
                resetForm();
            },
            onError: error => {
                setErrorMessage(error);
            },
            onComplete(resultProps) {
                setIsSubmitting(false);
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const onCancelClick = () => {
        onClose();
        resetForm();
    };

    const resetForm = () => {
        setUserName("");
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.currentTarget.value;
        setUserName(newUsername);
        setErrorMessage(
            newUsername
                ? ""
                : (siteMessage(
                      "Field_Required",
                      useEmailAsUserName ? translate("Email") : translate("Username"),
                  ) as string),
        );
    };

    if (!isResetPasswordModalOpen) {
        return null;
    }

    return (
        isResetPasswordModalOpen && (
            <GenericModal
                hasBackdrop={true}
                title={translate("Reset Password")}
                setIsOpen={setIsOpen}
                declineAction={() => onCancelClick()}
                cancelButtonTitle={translate("Return to sign in")}
                continueButtonTitle={translate("Send Email")}
                action={() => sendEmail(userName)}
                hasCancelButton={true}
                hasContinueButton={true}
            >
                <GridContainer {...styles.container}>
                    <GridItem {...styles.descriptionGridItem}>
                        <Typography {...styles.descriptionText}>
                            {useEmailAsUserName
                                ? siteMessage("ResetPassword_EmailInstructions")
                                : siteMessage("ResetPassword_Instructions")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            {...styles.userNameTextField}
                            label={useEmailAsUserName ? translate("Email") : translate("Username")}
                            name="userName"
                            value={userName}
                            backgroundColor="#ededed"
                            // eslint-disable-next-line spire/avoid-dynamic-translate
                            placeholder={useEmailAsUserName ? translate("Enter email") : translate("Enter username")}
                            error={errorMessage}
                            onChange={handleUsernameChange}
                            data-test-selector="resetPassword_userName"
                        ></TextField>
                    </GridItem>
                    <GridItem {...styles.reCaptchaGridItem}>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ForgotPassword" />
                    </GridItem>
                    {/* <GridItem {...styles.buttonsGridItem}>
                <Button customStyles={cancleButtonStyles} type="secondaryPWB" title={translate("Return to sign in")} onClick={onCancelClick} />
                <Button
                    type="primaryRoundButton"
                    title={translate("Send Email")}
                    disabled={isSubmitting}
                    onClick={() => sendEmail(userName)}
                    data-test-selector="resetPassword_submit"
                /> 
            </GridItem> */}
                </GridContainer>
            </GenericModal>
        )
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SignInResetPasswordForm);
